export const fontSizes = {
  // Responsive headings for page titles and major sections
  heading: {
    h1: {
      xs: '1.75rem',   // 28px - Mobile
      sm: '2.25rem',   // 36px - Tablet
      md: '2.5rem',    // 40px - Desktop
      lg: '3rem',      // 48px - Large desktop
    },
    h2: {
      xs: '1.5rem',    // 24px - Mobile
      sm: '1.75rem',   // 28px - Tablet
      md: '2rem',      // 32px - Desktop
      lg: '2.25rem',   // 36px - Large desktop
    },
    h3: {
      xs: '1.25rem',   // 20px - Mobile
      sm: '1.5rem',    // 24px - Tablet
      md: '1.75rem',   // 28px - Desktop
      lg: '2rem',      // 32px - Large desktop
    },
    h4: {
      xs: '1.125rem',  // 18px - Mobile
      sm: '1.25rem',   // 20px - Tablet
      md: '1.5rem',    // 24px - Desktop
      lg: '1.75rem',   // 28px - Large desktop
    },
    h5: {
      xs: '1rem',      // 16px - Mobile
      sm: '1.125rem',  // 18px - Tablet
      md: '1.25rem',   // 20px - Desktop
      lg: '1.5rem',    // 24px - Large desktop
    },
    h6: {
      xs: '0.875rem',  // 14px - Mobile
      sm: '1rem',      // 16px - Tablet
      md: '1.125rem',  // 18px - Desktop
      lg: '1.25rem',   // 20px - Large desktop
    },
  },

  // Responsive subheadings for supporting content
  subheading: {
    large: {
      xs: '1rem',      // 16px - Mobile
      sm: '1.125rem',  // 18px - Tablet
      md: '1.25rem',   // 20px - Desktop
    },
    medium: {
      xs: '0.875rem',  // 14px - Mobile
      sm: '1rem',      // 16px - Tablet
      md: '1.125rem',  // 18px - Desktop
    },
    small: {
      xs: '0.75rem',   // 12px - Mobile
      sm: '0.875rem',  // 14px - Tablet
      md: '1rem',      // 16px - Desktop
    },
  },

  // Responsive body text for main content
  body: {
    large: {
      xs: '0.875rem',  // 14px - Mobile
      sm: '1rem',      // 16px - Tablet
      md: '1.125rem',  // 18px - Desktop
    },
    medium: {
      xs: '0.75rem',   // 12px - Mobile
      sm: '0.875rem',  // 14px - Tablet
      md: '1rem',      // 16px - Desktop
    },
    small: {
      xs: '0.625rem',  // 10px - Mobile
      sm: '0.75rem',   // 12px - Tablet
      md: '0.875rem',  // 14px - Desktop
    },
  },

  // Responsive labels for forms, inputs, and UI elements
  label: {
    large: {
      xs: '0.75rem',   // 12px - Mobile
      sm: '0.875rem',  // 14px - Tablet
      md: '1rem',      // 16px - Desktop
    },
    medium: {
      xs: '0.625rem',  // 10px - Mobile
      sm: '0.75rem',   // 12px - Tablet
      md: '0.875rem',  // 14px - Desktop
    },
    small: {
      xs: '0.5rem',    // 8px - Mobile
      sm: '0.625rem',  // 10px - Tablet
      md: '0.75rem',   // 12px - Desktop
    },
  },

  // Responsive special typography
  caption: {
    xs: '0.625rem',   // 10px - Mobile
    sm: '0.75rem',    // 12px - Tablet
    md: '0.875rem',   // 14px - Desktop
  },
  button: {
    xs: '0.75rem',    // 12px - Mobile
    sm: '0.875rem',   // 14px - Tablet
    md: '1rem',       // 16px - Desktop
  },
  overline: {
    xs: '0.5rem',     // 8px - Mobile
    sm: '0.625rem',   // 10px - Tablet
    md: '0.75rem',    // 12px - Desktop
  },

  // Responsive display text for hero sections and marketing
  display: {
    large: {
      xs: '2rem',     // 32px - Mobile
      sm: '2.5rem',   // 40px - Tablet
      md: '3rem',     // 48px - Desktop
      lg: '3.5rem',   // 56px - Large desktop
    },
    medium: {
      xs: '1.75rem',  // 28px - Mobile
      sm: '2.25rem',  // 36px - Tablet
      md: '2.75rem',  // 44px - Desktop
      lg: '3rem',     // 48px - Large desktop
    },
    small: {
      xs: '1.5rem',   // 24px - Mobile
      sm: '2rem',     // 32px - Tablet
      md: '2.25rem',  // 36px - Desktop
      lg: '2.5rem',   // 40px - Large desktop
    },
  },
} as const;

// Line heights for optimal readability
export const lineHeights = {
  tight: 1.2,      // For headings
  normal: 1.5,     // For body text
  relaxed: 1.6,    // For large body text
  loose: 1.8,      // For captions and small text
} as const;

// Font weights for typography hierarchy
export const fontWeights = {
  light: 300,
  normal: 400,
  medium: 500,
  semibold: 600,
  bold: 700,
  extrabold: 800,
} as const;

// Letter spacing for different text types
export const letterSpacing = {
  tight: '-0.025em',    // For large headings
  normal: '0em',        // For body text
  wide: '0.025em',      // For labels and buttons
  wider: '0.05em',      // For overlines and categories
  widest: '0.1em',      // For emphasis
} as const;

// Responsive spacing scale for consistent layout
export const spacing = {
  xs: {
    xs: '0.5rem',   // 8px - Mobile
    sm: '0.75rem',  // 12px - Tablet
    md: '1rem',     // 16px - Desktop
  },
  sm: {
    xs: '0.75rem',  // 12px - Mobile
    sm: '1rem',     // 16px - Tablet
    md: '1.25rem',  // 20px - Desktop
  },
  md: {
    xs: '1rem',     // 16px - Mobile
    sm: '1.5rem',   // 24px - Tablet
    md: '2rem',     // 32px - Desktop
  },
  lg: {
    xs: '1.5rem',   // 24px - Mobile
    sm: '2rem',     // 32px - Tablet
    md: '2.5rem',   // 40px - Desktop
  },
  xl: {
    xs: '2rem',     // 32px - Mobile
    sm: '2.5rem',   // 40px - Tablet
    md: '3rem',     // 48px - Desktop
  },
  xxl: {
    xs: '2.5rem',   // 40px - Mobile
    sm: '3rem',     // 48px - Tablet
    md: '4rem',     // 64px - Desktop
  },
} as const;

// Touch-friendly spacing for mobile
export const touchSpacing = {
  button: {
    xs: '12px',     // Mobile - minimum 44px touch target
    sm: '14px',     // Tablet
    md: '16px',     // Desktop
  },
  icon: {
    xs: '8px',      // Mobile
    sm: '10px',     // Tablet
    md: '12px',     // Desktop
  },
  input: {
    xs: '16px',     // Mobile - comfortable input padding
    sm: '18px',     // Tablet
    md: '20px',     // Desktop
  },
} as const;

export const borderRadius = {
  small: '4px',
  medium: '8px',
  large: '12px',
  round: '50%',
} as const;

export const breakpoints = {
  xs: '0px',        // Extra small devices (phones)
  sm: '600px',      // Small devices (tablets)
  md: '900px',      // Medium devices (small laptops)
  lg: '1200px',     // Large devices (desktops)
  xl: '1536px',     // Extra large devices (large desktops)
} as const;

// Material-UI compatible breakpoints
export const muiBreakpoints = {
  xs: 0,
  sm: 600,
  md: 900,
  lg: 1200,
  xl: 1536,
} as const;

export type FontSizes = typeof fontSizes;
export type LineHeights = typeof lineHeights;
export type FontWeights = typeof fontWeights;
export type LetterSpacing = typeof letterSpacing;
export type Spacing = typeof spacing;
export type BorderRadius = typeof borderRadius;
export type Breakpoints = typeof breakpoints;
